// Simple toast notification system
// This is a basic implementation - you can replace it with a library like react-toastify

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastOptions {
  duration?: number;
  action?: ToastAction;
}

class Toast {
  private showToast(message: string, type: ToastType, options?: ToastOptions) {
    // Create toast container
    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;

    // Create message element
    const messageEl = document.createElement('span');
    messageEl.textContent = message;
    messageEl.style.cssText = 'flex: 1;';
    toast.appendChild(messageEl);

    // Add action button if provided
    if (options?.action) {
      const actionButton = document.createElement('button');
      actionButton.textContent = options.action.label;
      actionButton.onclick = (e) => {
        e.stopPropagation();
        options.action!.onClick();
        // Remove toast after action is clicked
        toast.style.animation = 'slideOut 0.3s ease-in';
        setTimeout(() => {
          if (toast.parentNode) {
            document.body.removeChild(toast);
          }
        }, 300);
      };
      actionButton.style.cssText = `
        margin-left: 12px;
        padding: 4px 12px;
        border: 1px solid rgba(255, 255, 255, 0.5);
        border-radius: 20px;
        background: rgba(255, 255, 255, 0.2);
        color: white;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        white-space: nowrap;
      `;
      actionButton.onmouseenter = () => {
        actionButton.style.background = 'rgba(255, 255, 255, 0.3)';
      };
      actionButton.onmouseleave = () => {
        actionButton.style.background = 'rgba(255, 255, 255, 0.2)';
      };
      toast.appendChild(actionButton);
    }

    // Add styles - clean pill design with flex layout
    toast.style.cssText = `
      position: fixed;
      top: 24px;
      right: 24px;
      padding: 12px 20px;
      border-radius: 50px;
      color: white;
      font-size: 14px;
      font-weight: 500;
      z-index: 9999;
      animation: slideIn 0.3s ease-out;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      max-width: 500px;
      display: flex;
      align-items: center;
      gap: 8px;
    `;

    // Set background color based on type - simple green/red pills
    switch (type) {
      case 'success':
        toast.style.backgroundColor = '#10b981';
        break;
      case 'error':
        toast.style.backgroundColor = '#ef4444';
        break;
      case 'info':
        toast.style.backgroundColor = '#3b82f6';
        break;
      case 'warning':
        toast.style.backgroundColor = '#f59e0b';
        break;
    }

    // Add animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      @keyframes slideOut {
        from {
          transform: translateX(0);
          opacity: 1;
        }
        to {
          transform: translateX(100%);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(style);

    // Add to document
    document.body.appendChild(toast);

    // Remove after duration
    const duration = options?.duration || 3000;
    setTimeout(() => {
      toast.style.animation = 'slideOut 0.3s ease-in';
      setTimeout(() => {
        document.body.removeChild(toast);
        document.head.removeChild(style);
      }, 300);
    }, duration);
  }

  success(message: string, options?: ToastOptions) {
    this.showToast(message, 'success', options);
  }

  error(message: string, options?: ToastOptions) {
    this.showToast(message, 'error', options);
  }

  info(message: string, options?: ToastOptions) {
    this.showToast(message, 'info', options);
  }

  warning(message: string, options?: ToastOptions) {
    this.showToast(message, 'warning', options);
  }
}

export const toast = new Toast();
